import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow, GameshowNode } from '../../schemas/gameshow.js';

import { asRecord } from '../../activities/types.js';
import { toPointer } from '../../core/issues.js';
import {
  GAMESHOW_CORRECT_DELTA,
  GAMESHOW_QUIZ_ACTIVITY_TYPES,
  GAMESHOW_ROUND_COUNT,
} from '../../schemas/gameshowConstants.js';
import { hasNonEmptyDialogue } from './_helpers.js';

const QUIZ_TYPES = new Set<string>(GAMESHOW_QUIZ_ACTIVITY_TYPES);
const ALLOWED_OUTCOMES = ['correct', 'wrong'] as const;

const isQuizQuestionNode = (node: GameshowNode): boolean => {
  if (!node.activity) {
    return false;
  }
  if (!QUIZ_TYPES.has(node.activity.type)) {
    return false;
  }
  const setup = (node.activity.setup ?? {}) as Record<string, unknown>;
  return setup.revealAnswer !== true;
};

const filled = (value: unknown): boolean =>
  Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';

/** Whether a round names its right answer, which makes the player wait for it unless told not to. */
const marksRightAnswer = (node: GameshowNode): boolean => {
  const setup = (node.activity?.setup ?? {}) as Record<string, unknown>;
  return ['correctAnswerSingle', 'correctAnswerMultiple', 'correctAnswer', 'correctAnswerOrder'].some(key => filled(setup[key]));
};

interface ScoreDeltaLike {
  characterId?: unknown;
  delta?: unknown;
}

export const quizRounds: Rule<Gameshow> = {
  code: 'gameshow.quiz-rounds',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const nodesById = new Map(input.nodes.map((n, idx) => [n.id, { node: n, idx }]));
    const quizNodes = input.nodes
      .map((node, idx) => ({ node, idx }))
      .filter(({ node }) => isQuizQuestionNode(node));

    if (quizNodes.length !== GAMESHOW_ROUND_COUNT) {
      const path: (string | number)[] = ['nodes'];
      issues.push({
        code: 'gameshow.quiz-rounds.count',
        severity: 'error',
        message: `Expected exactly ${GAMESHOW_ROUND_COUNT} quiz rounds, found ${quizNodes.length}.`,
        path,
        pointer: toPointer(path),
        currentValue: quizNodes.length,
        constraint: { kind: 'range', detail: `exactly ${GAMESHOW_ROUND_COUNT}` },
      });
    }

    for (let i = 1; i <= GAMESHOW_ROUND_COUNT; i++) {
      const id = `q${i}`;
      if (!nodesById.has(id)) {
        const path: (string | number)[] = ['nodes'];
        issues.push({
          code: 'gameshow.quiz-rounds.missing',
          severity: 'error',
          message: `Missing expected quiz round node '${id}'.`,
          path,
          pointer: toPointer(path),
          constraint: { kind: 'custom', detail: `node '${id}' required` },
        });
      }
    }

    const expectedQuizNodes = quizNodes.filter(({ node }) => /^q\d+$/.test(node.id));
    const types = expectedQuizNodes.map(({ node }) => node.activity!.type);
    const uniqueTypes = new Set(types);
    if (expectedQuizNodes.length === GAMESHOW_ROUND_COUNT && uniqueTypes.size !== GAMESHOW_ROUND_COUNT) {
      const path: (string | number)[] = ['nodes'];
      issues.push({
        code: 'gameshow.quiz-rounds.duplicate-activity-type',
        severity: 'error',
        message: `Each round must use a different quiz activity type. Found ${uniqueTypes.size} unique across ${GAMESHOW_ROUND_COUNT}: [${types.join(', ')}].`,
        path,
        pointer: toPointer(path),
        currentValue: types,
        constraint: { kind: 'unique', detail: 'one type per round' },
      });
    }

    const player = input.characters.find(c => c.role === 'self');
    const contestant = input.characters.find(c => c.role === 'rival');
    const characterIds = new Set(input.characters.map(c => c.id));

    for (const { node, idx } of quizNodes) {
      const setup = (node.activity?.setup ?? {}) as Record<string, unknown>;

      // Left out, the player defaults it to true: a round that names its right answer then waits
      // for it and finishes complete, never correct or wrong, and the show stops there.
      if (setup.requireCorrectAnswer === true || (marksRightAnswer(node) && setup.requireCorrectAnswer !== false)) {
        const path: (string | number)[] = ['nodes', idx, 'activity', 'setup', 'requireCorrectAnswer'];
        issues.push({
          code: 'gameshow.quiz-rounds.require-correct-answer',
          severity: 'error',
          message: `Node '${node.id}': a quiz round that names its right answer must set 'requireCorrectAnswer' to false, or the round never finishes correct or wrong.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: setup.requireCorrectAnswer ?? '(omitted)',
          constraint: { kind: 'enum', allowedValues: ['false'] },
        });
      }

      if (!hasNonEmptyDialogue(node.narratorDialogue)) {
        const path: (string | number)[] = ['nodes', idx, 'narratorDialogue'];
        issues.push({
          code: 'gameshow.quiz-rounds.missing-narrator',
          severity: 'error',
          message: `Node '${node.id}': quiz round node must have narratorDialogue (the host asks the question).`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'narratorDialogue required' },
        });
      }

      const outcomeEffects = node.outcomeEffects ?? {};
      const outcomeKeys = Object.keys(outcomeEffects);
      const unexpectedOutcomes = outcomeKeys.filter(
        k => !ALLOWED_OUTCOMES.includes(k as typeof ALLOWED_OUTCOMES[number]),
      );
      if (unexpectedOutcomes.length > 0) {
        const path: (string | number)[] = ['nodes', idx, 'outcomeEffects'];
        issues.push({
          code: 'gameshow.quiz-rounds.non-standard-outcome',
          severity: 'error',
          message: `Node '${node.id}': outcomeEffects has non-standard keys [${unexpectedOutcomes.join(', ')}]. Use only 'correct' and 'wrong'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: outcomeKeys,
          constraint: { kind: 'enum', allowedValues: [...ALLOWED_OUTCOMES] },
        });
      }

      for (const outcome of ALLOWED_OUTCOMES) {
        const effect = outcomeEffects[outcome];
        const deltas = Array.isArray(effect?.scoreDeltas) ? (effect.scoreDeltas as ScoreDeltaLike[]) : null;
        if (!deltas) {
          const path: (string | number)[] = ['nodes', idx, 'outcomeEffects', outcome, 'scoreDeltas'];
          issues.push({
            code: 'gameshow.quiz-rounds.missing-score-deltas',
            severity: 'error',
            message: `Node '${node.id}': outcomeEffects.${outcome}.scoreDeltas missing.`,
            path,
            pointer: toPointer(path),
            locator: { nodeId: node.id },
            constraint: { kind: 'shape', detail: 'scoreDeltas array required' },
          });
          continue;
        }

        let sawPlayer = false;
        let sawContestant = false;
        deltas.forEach((d, dIdx) => {
          const characterId = typeof d.characterId === 'string' ? d.characterId : undefined;
          const delta = typeof d.delta === 'number' ? d.delta : undefined;
          const basePath: (string | number)[] = ['nodes', idx, 'outcomeEffects', outcome, 'scoreDeltas', dIdx];

          if (characterId && !characterIds.has(characterId)) {
            const path = [...basePath, 'characterId'];
            issues.push({
              code: 'gameshow.quiz-rounds.score-delta-unknown-character',
              severity: 'error',
              message: `Node '${node.id}': scoreDelta for outcome '${outcome}' references unknown characterId '${characterId}'.`,
              path,
              pointer: toPointer(path),
              locator: { nodeId: node.id },
              currentValue: characterId,
              constraint: { kind: 'reference', allowedValues: Array.from(characterIds) },
            });
          }
          if (player && characterId === player.id) {
            sawPlayer = true;
          }
          if (contestant && characterId === contestant.id) {
            sawContestant = true;
          }

          if (delta === undefined) {
            const path = [...basePath, 'delta'];
            issues.push({
              code: 'gameshow.quiz-rounds.score-delta-missing',
              severity: 'error',
              message: `Node '${node.id}': scoreDelta for '${characterId ?? '?'}' (outcome '${outcome}') missing 'delta'.`,
              path,
              pointer: toPointer(path),
              locator: { nodeId: node.id },
              constraint: { kind: 'shape', detail: '\'delta\' (number) required' },
            });
          }
          else if (delta !== 0 && delta !== GAMESHOW_CORRECT_DELTA) {
            const path = [...basePath, 'delta'];
            issues.push({
              code: 'gameshow.quiz-rounds.score-delta-value',
              severity: 'error',
              message: `Node '${node.id}': scoreDelta for '${characterId ?? '?'}' (outcome '${outcome}') has delta ${delta} — must be 0 or ${GAMESHOW_CORRECT_DELTA}.`,
              path,
              pointer: toPointer(path),
              locator: { nodeId: node.id },
              currentValue: delta,
              constraint: { kind: 'enum', allowedValues: ['0', String(GAMESHOW_CORRECT_DELTA)] },
            });
          }

          if (player && characterId === player.id && delta !== undefined) {
            if (outcome === 'correct' && delta !== GAMESHOW_CORRECT_DELTA) {
              const path = [...basePath, 'delta'];
              issues.push({
                code: 'gameshow.quiz-rounds.player-correct-delta',
                severity: 'error',
                message: `Node '${node.id}': player scoreDelta on 'correct' must be ${GAMESHOW_CORRECT_DELTA}, found ${delta}.`,
                path,
                pointer: toPointer(path),
                locator: { nodeId: node.id },
                currentValue: delta,
                constraint: { kind: 'enum', allowedValues: [String(GAMESHOW_CORRECT_DELTA)] },
              });
            }
            if (outcome === 'wrong' && delta !== 0) {
              const path = [...basePath, 'delta'];
              issues.push({
                code: 'gameshow.quiz-rounds.player-wrong-delta',
                severity: 'error',
                message: `Node '${node.id}': player scoreDelta on 'wrong' must be 0, found ${delta}.`,
                path,
                pointer: toPointer(path),
                locator: { nodeId: node.id },
                currentValue: delta,
                constraint: { kind: 'enum', allowedValues: ['0'] },
              });
            }
          }
        });

        if (player && !sawPlayer) {
          const path: (string | number)[] = ['nodes', idx, 'outcomeEffects', outcome, 'scoreDeltas'];
          issues.push({
            code: 'gameshow.quiz-rounds.score-delta-missing-player',
            severity: 'error',
            message: `Node '${node.id}': outcomeEffects.${outcome}.scoreDeltas missing entry for the player ('${player.id}').`,
            path,
            pointer: toPointer(path),
            locator: { nodeId: node.id },
            constraint: { kind: 'custom', detail: 'player score delta required' },
          });
        }
        if (contestant && !sawContestant) {
          const path: (string | number)[] = ['nodes', idx, 'outcomeEffects', outcome, 'scoreDeltas'];
          issues.push({
            code: 'gameshow.quiz-rounds.score-delta-missing-contestant',
            severity: 'error',
            message: `Node '${node.id}': outcomeEffects.${outcome}.scoreDeltas missing entry for the contestant ('${contestant.id}').`,
            path,
            pointer: toPointer(path),
            locator: { nodeId: node.id },
            constraint: { kind: 'custom', detail: 'contestant score delta required' },
          });
        }
      }

      const routes = node.exitRoutes ?? {};
      const routeKeys = Object.keys(routes);
      const unexpectedRoutes = routeKeys.filter(
        k => !ALLOWED_OUTCOMES.includes(k as typeof ALLOWED_OUTCOMES[number]),
      );
      if (unexpectedRoutes.length > 0) {
        const path: (string | number)[] = ['nodes', idx, 'exitRoutes'];
        issues.push({
          code: 'gameshow.quiz-rounds.non-standard-exit',
          severity: 'error',
          message: `Node '${node.id}': exitRoutes has non-standard keys [${unexpectedRoutes.join(', ')}]. Use only 'correct' and 'wrong'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: routeKeys,
          constraint: { kind: 'enum', allowedValues: [...ALLOWED_OUTCOMES] },
        });
      }
      if (!routes.correct) {
        const path: (string | number)[] = ['nodes', idx, 'exitRoutes'];
        issues.push({
          code: 'gameshow.quiz-rounds.missing-correct-route',
          severity: 'error',
          message: `Node '${node.id}': missing exitRoute 'correct'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'exitRoutes.correct required' },
        });
      }
      if (!routes.wrong) {
        const path: (string | number)[] = ['nodes', idx, 'exitRoutes'];
        issues.push({
          code: 'gameshow.quiz-rounds.missing-wrong-route',
          severity: 'error',
          message: `Node '${node.id}': missing exitRoute 'wrong'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'exitRoutes.wrong required' },
        });
      }

      const opts = setup.options;
      if (Array.isArray(opts)) {
        opts.forEach((o, oIdx) => {
          const outcomeId = asRecord(o).outcomeId;
          if (typeof outcomeId === 'string' && !ALLOWED_OUTCOMES.includes(outcomeId as typeof ALLOWED_OUTCOMES[number])) {
            const path: (string | number)[] = ['nodes', idx, 'activity', 'setup', 'options', oIdx, 'outcomeId'];
            issues.push({
              code: 'gameshow.quiz-rounds.non-standard-option-outcome',
              severity: 'error',
              message: `Node '${node.id}': option outcomeId '${outcomeId}' must be 'correct' or 'wrong'.`,
              path,
              pointer: toPointer(path),
              locator: { nodeId: node.id },
              currentValue: outcomeId,
              constraint: { kind: 'enum', allowedValues: [...ALLOWED_OUTCOMES] },
            });
          }
        });
      }
    }

    return issues;
  },
};
